import React, { MouseEvent, useState } from 'react';
import ListIcon from '@mui/icons-material/List';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SettingsIcon from '@mui/icons-material/Settings';
import { Menu, MenuItem, useMediaQuery, useTheme } from '@mui/material';

import { TooltipIconButton } from '@paragon/client-tools';
import { LayoutModel, Project } from '@paragon/contract-library';

import { requestContextMenu } from '../../store/actions/contextMenuActions';
import { showChooseProjectDialog } from '../../store/actions/popupActions';
import { inViewOnlyMode } from '../../store/selectors/viewOnlyModeSelectors';
import { useAppDispatch, useAppSelector } from '../hooks';
import { useSettingsOperations } from '../misc/menus/shared-menu-items/SettingsSubMenu';
import { ContextToolbarContainer, ContextToolbarTitle } from './contextToolbarComponents';

interface Props {
  readonly project: Project & LayoutModel;
}

export function ProjectContextHeaderToolbar({ project }: Props) {
  const dispatch = useAppDispatch();

  const [settingsAnchorEl, setSettingsAnchorEl] = useState<HTMLElement | null>(null);
  const settingsOperations = useSettingsOperations(project ? { type: 'project', item: project } : null);

  const viewOnly = useAppSelector(inViewOnlyMode);

  const theme = useTheme();
  const hasRoomForExtraButtons = useMediaQuery(theme.breakpoints.up('xl'));

  const openContextMenu = (event: MouseEvent) => {
    dispatch(requestContextMenu(event, { type: 'project', guid: project.guid }, { outsideScene: true }));
  };

  return (
    <ContextToolbarContainer>
      {hasRoomForExtraButtons && (
        <TooltipIconButton
          enableInteractive
          title="Choose/Create Project"
          onClick={() => void dispatch(showChooseProjectDialog())}
        >
          <ListIcon />
        </TooltipIconButton>
      )}

      <ContextToolbarTitle
        onContextMenu={(event) => {
          event.preventDefault();
          openContextMenu(event);
        }}
      >
        {project.name}
      </ContextToolbarTitle>

      {hasRoomForExtraButtons && settingsOperations.length > 0 && (
        <TooltipIconButton
          enableInteractive
          title="Project Settings"
          onClick={(event) => setSettingsAnchorEl(event.currentTarget)}
        >
          <SettingsIcon />
        </TooltipIconButton>
      )}

      {!viewOnly && (
        <TooltipIconButton enableInteractive title="Project Menu" onClick={openContextMenu}>
          <MoreVertIcon />
        </TooltipIconButton>
      )}

      {settingsAnchorEl != null && settingsOperations.length > 0 && (
        <Menu open anchorEl={settingsAnchorEl} onClose={() => setSettingsAnchorEl(null)}>
          {settingsOperations.map((operation) => (
            <MenuItem
              key={operation.key}
              disabled={operation.disabled}
              onClick={() => {
                setSettingsAnchorEl(null);
                dispatch(operation.getActionToDispatch());
              }}
            >
              {operation.text}
            </MenuItem>
          ))}
        </Menu>
      )}
    </ContextToolbarContainer>
  );
}
